import React from 'react';
import { Link } from 'react-router-dom';
import internalLinks from '../../../data/internal-links.json';

/**
 * Booking-driven related-services section.
 * Renders links to high-LTV pages (brand + service) that historically
 * convert organic visitors. Booking-driven plan 2026-04-28: skip blog
 * cohort (0.16% conv) and concentrate link equity on pages that earn
 * bookings.
 *
 * Variants:
 *   variant="city"     — show top brand pages + top service pages
 *   variant="brand"    — show top cities (used on /brands/*)
 *   variant="generic"  — show top brand pages (used on programmatic, blogs)
 *   variant="keyword"  — show top cities (used on keyword-landing pages so
 *                       car-keyword traffic funnels link equity into the
 *                       under-ranking city pages, e.g. /city/bengaluru
 *                       which sits at pos 6.7 with 109k impr/30d).
 */
const RelatedTopServices = ({ variant = 'generic', heading, kicker }) => {
  const brandLinks = internalLinks.city_to_brand || [];
  const serviceLinks = internalLinks.city_to_service || [];
  const cityLinks = internalLinks.brand_to_city || [];
  const keywordToCityLinks = internalLinks.keyword_to_city || [];

  if (variant === 'brand') {
    if (cityLinks.length === 0) return null;
    return (
      <section className="rr-related-services rr-section--gray">
        <div className="rr-container">
          <div className="rr-related-services__head">
            {kicker && <span className="rr-related-services__kicker">{kicker}</span>}
            <h2>{heading || 'Doorstep Service in Top Cities'}</h2>
          </div>
          <div className="rr-related-services__grid">
            {cityLinks.map((l) => (
              <Link key={l.href} to={l.href} className="rr-related-services__card">
                <span className="rr-related-services__label">{l.label}</span>
                <span className="rr-related-services__cta">View Service →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (variant === 'city') {
    return (
      <section className="rr-related-services rr-section--gray">
        <div className="rr-container">
          <div className="rr-related-services__head">
            {kicker && <span className="rr-related-services__kicker">{kicker}</span>}
            <h2>{heading || 'Popular Brand Service & Repairs'}</h2>
            <p className="rr-related-services__sub">Doorstep service for the brands our customers book most often.</p>
          </div>
          <div className="rr-related-services__grid">
            {brandLinks.slice(0, 6).map((l) => (
              <Link key={l.href} to={l.href} className="rr-related-services__card">
                <span className="rr-related-services__label">{l.label}</span>
                <span className="rr-related-services__cta">View Service →</span>
              </Link>
            ))}
          </div>
          {serviceLinks.length > 0 && (
            <>
              <div className="rr-related-services__head" style={{ marginTop: 32 }}>
                <h3>Popular Doorstep Services</h3>
              </div>
              <div className="rr-related-services__grid">
                {serviceLinks.slice(0, 6).map((l) => (
                  <Link key={l.href} to={l.href} className="rr-related-services__card">
                    <span className="rr-related-services__label">{l.label}</span>
                    <span className="rr-related-services__cta">Book →</span>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    );
  }

  if (variant === 'keyword') {
    // Funnels link equity from car-keyword pages into city pages that earn
    // bookings. Falls back to brand_to_city if keyword_to_city not populated.
    const keywordCityLinks = keywordToCityLinks.length ? keywordToCityLinks : cityLinks;
    if (keywordCityLinks.length === 0 && brandLinks.length === 0) return null;
    return (
      <section className="rr-related-services rr-section--gray">
        <div className="rr-container">
          <div className="rr-related-services__head">
            {kicker && <span className="rr-related-services__kicker">{kicker}</span>}
            <h2>{heading || 'Doorstep Service in Top Cities'}</h2>
          </div>
          <div className="rr-related-services__grid">
            {keywordCityLinks.slice(0, 6).map((l) => (
              <Link key={l.href} to={l.href} className="rr-related-services__card">
                <span className="rr-related-services__label">{l.label}</span>
                <span className="rr-related-services__cta">View City →</span>
              </Link>
            ))}
          </div>
          {brandLinks.length > 0 && (
            <>
              <div className="rr-related-services__head" style={{ marginTop: 32 }}>
                <h3>Popular Brand Service & Repairs</h3>
              </div>
              <div className="rr-related-services__grid">
                {brandLinks.slice(0, 6).map((l) => (
                  <Link key={l.href} to={l.href} className="rr-related-services__card">
                    <span className="rr-related-services__label">{l.label}</span>
                    <span className="rr-related-services__cta">View Service →</span>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    );
  }

  // generic
  if (brandLinks.length === 0) return null;
  return (
    <section className="rr-related-services rr-section--gray">
      <div className="rr-container">
        <div className="rr-related-services__head">
          {kicker && <span className="rr-related-services__kicker">{kicker}</span>}
          <h2>{heading || 'Popular Doorstep Service & Repairs'}</h2>
        </div>
        <div className="rr-related-services__grid">
          {brandLinks.slice(0, 6).map((l) => (
            <Link key={l.href} to={l.href} className="rr-related-services__card">
              <span className="rr-related-services__label">{l.label}</span>
              <span className="rr-related-services__cta">View Service →</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedTopServices;
